/**
 * R2DataService - Cliente para datos geodatos desde Cloudflare R2
 * 
 * Proporciona acceso a datasets DERA/INE/NGA desde R2 Worker con:
 * - Circuit Breaker para fallback automático a bundle estático
 * - Caché IndexedDB (Dexie) para acceso offline
 * - Compresión gzip automática
 * - Métricas de estado y rendimiento
 * 
 * Arquitectura de fallback:
 * 1. Caché IndexedDB (más rápido, offline)
 * 2. R2 Worker (datos actualizados)
 * 3. Bundle estático /public/data/ (último recurso)
 * 
 * @module services/data/R2DataService
 * @version 1.0.0
 * @date 2026-01-28
 */

import Dexie, { type Table } from 'dexie';
import { fetchWithBackoff } from '../../utils/fetchWithBackoff';
import { WORKER_BASE, WORKER_BASE_V1 } from '@/lib/config/workerEndpoints';

// =============================================================================
// CONSTANTES
// =============================================================================

/**
 * URL base del Worker R2, ruta /v1 (alias local).
 * Fuente única de verdad: `@/lib/config/workerEndpoints` (`WORKER_BASE_V1`).
 */
const R2_BASE_URL = WORKER_BASE_V1;

/** URL fallback del bundle estático */
const STATIC_BUNDLE_BASE = '/data';

/** Timeout para peticiones R2 (ms) */
const R2_TIMEOUT = 3_000; // AUD-E04: reducido de 15000 ms; la cascada de fallback es el mecanismo de resiliencia

/** TTL de caché IndexedDB (24 horas) */
const CACHE_TTL = 24 * 60 * 60 * 1000;

/** Umbral de errores para abrir Circuit Breaker */
const CIRCUIT_BREAKER_THRESHOLD = 3;

/** Tiempo de reset del Circuit Breaker (ms) */
const CIRCUIT_BREAKER_RESET_TIME = 60000;

// =============================================================================
// TIPOS
// =============================================================================

/** Datasets disponibles en R2 */
export type R2Dataset = 
  | 'dera/all-dera'
  | 'dera/health'
  | 'dera/education'
  | 'dera/security'
  | 'dera/municipal'
  | 'dera/energy'
  | 'dera/emergency'
  | 'ine/municipios'
  | 'nga/nga-all'
  | 'nga/nga-landform'
  | 'nga/nga-populatedplace'
  | 'nga/nga-hydrography'
  | 'nga/nga-landcover'
  | 'nga/nga-transportnetwork';

/** Estado del Circuit Breaker */
export type CircuitState = 'CLOSED' | 'OPEN' | 'HALF_OPEN';

/** Entrada de caché IndexedDB */
interface CacheEntry<T> {
  /** ID del dataset (primary key) */
  datasetId: string;
  /** Datos deserializados */
  data: T;
  /** Timestamp de última actualización */
  updatedAt: number;
  /** ETag para validación de caché */
  etag: string | null;
  /** Tamaño en bytes */
  sizeBytes: number;
  /** Fuente de datos */
  source: 'r2' | 'static';
}

/** Resultado de fetch con metadatos */
export interface FetchResult<T> {
  /** Datos obtenidos */
  data: T;
  /** Fuente de datos */
  source: 'cache' | 'r2' | 'static';
  /** Tiempo de respuesta (ms) */
  responseTime: number;
  /** ¿Circuit breaker activo? */
  circuitBreakerActive: boolean;
  /** ETag del recurso */
  etag: string | null;
}

/** Estadísticas del servicio */
export interface R2ServiceStats {
  /** Total de peticiones */
  totalRequests: number;
  /** Hits de caché */
  cacheHits: number;
  /** Peticiones a R2 */
  r2Requests: number;
  /** Peticiones a bundle estático */
  staticRequests: number;
  /** Errores R2 */
  r2Errors: number;
  /** Estado del Circuit Breaker */
  circuitState: CircuitState;
  /** Errores consecutivos */
  consecutiveErrors: number;
  /** Datasets en caché */
  cachedDatasets: number;
  /** Tamaño total caché (bytes) */
  totalCacheSize: number;
}

/** Manifiesto de R2 */
interface R2Manifest {
  version: string;
  updated: string;
  datasets: Record<string, {
    files: Array<{ path: string; size: number; status: string }>;
    totalRecords: number;
  }>;
  status: { healthy: boolean };
}

// =============================================================================
// BASE DE DATOS INDEXEDDB
// =============================================================================

/**
 * Schema Dexie para caché R2
 */
class R2CacheDB extends Dexie {
  datasets!: Table<CacheEntry<unknown>, string>;
  meta!: Table<{ key: string; value: string | number | boolean }, string>;

  constructor() {
    super('R2CacheDB');
    
    this.version(1).stores({
      datasets: 'datasetId, updatedAt, source',
      meta: 'key'
    });
  }
}

// =============================================================================
// CIRCUIT BREAKER
// =============================================================================

/**
 * Implementación de Circuit Breaker para R2
 */
class CircuitBreaker {
  private state: CircuitState = 'CLOSED';
  private errorCount = 0;
  private lastErrorTime = 0;
  private readonly threshold: number;
  private readonly resetTime: number;

  constructor(threshold = CIRCUIT_BREAKER_THRESHOLD, resetTime = CIRCUIT_BREAKER_RESET_TIME) {
    this.threshold = threshold;
    this.resetTime = resetTime;
  }

  /** Registra un error y potencialmente abre el circuito */
  recordError(): void {
    this.errorCount++;
    this.lastErrorTime = Date.now();
    
    if (this.errorCount >= this.threshold) {
      this.state = 'OPEN';
    }
  }

  /** Registra un éxito y cierra el circuito */
  recordSuccess(): void {
    this.errorCount = 0;
    this.state = 'CLOSED';
  }

  /** Verifica si se puede hacer una petición */
  canRequest(): boolean {
    if (this.state === 'CLOSED') return true;
    
    if (this.state === 'OPEN') {
      // Verificar si ha pasado suficiente tiempo para intentar de nuevo
      if (Date.now() - this.lastErrorTime >= this.resetTime) {
        this.state = 'HALF_OPEN';
        return true;
      }
      return false;
    }
    
    // HALF_OPEN: permitir un intento
    return true;
  }

  /** Obtiene el estado actual */
  getState(): CircuitState {
    return this.state;
  }

  /** Obtiene el contador de errores */
  getErrorCount(): number {
    return this.errorCount;
  }

  /** Resetea el circuit breaker */
  reset(): void {
    this.state = 'CLOSED';
    this.errorCount = 0;
    this.lastErrorTime = 0;
  }
}

// =============================================================================
// SERVICIO PRINCIPAL
// =============================================================================

/**
 * Cliente de datos R2 con Circuit Breaker y caché offline
 */
export class R2DataService {
  private db: R2CacheDB;
  private circuitBreaker: CircuitBreaker;
  private stats: R2ServiceStats;
  private dbReady: Promise<void>;
  private manifest: R2Manifest | null = null;

  constructor() {
    this.db = new R2CacheDB();
    this.circuitBreaker = new CircuitBreaker();
    this.stats = this.initStats();
    this.dbReady = this.initDB();
  }

  private initStats(): R2ServiceStats {
    return {
      totalRequests: 0,
      cacheHits: 0,
      r2Requests: 0,
      staticRequests: 0,
      r2Errors: 0,
      circuitState: 'CLOSED',
      consecutiveErrors: 0,
      cachedDatasets: 0,
      totalCacheSize: 0
    };
  }

  private async initDB(): Promise<void> {
    try {
      await this.db.open();
    } catch (error) {
      console.warn('[R2DataService] IndexedDB no disponible:', error);
    }
  }

  /**
   * Obtiene un dataset con fallback automático
   * 
   * Orden de prioridad:
   * 1. Caché IndexedDB (si no expirado)
   * 2. R2 Worker (si circuit breaker cerrado)
   * 3. Bundle estático (fallback final)
   */
  async fetchDataset<T>(dataset: R2Dataset): Promise<FetchResult<T>> {
    const startTime = performance.now();
    this.stats.totalRequests++;

    await this.dbReady;

    // 1. Intentar caché primero
    const cached = await this.getFromCache<T>(dataset);
    if (cached && !this.isCacheExpired(cached.updatedAt)) {
      this.stats.cacheHits++;
      return {
        data: cached.data as T,
        source: 'cache',
        responseTime: performance.now() - startTime,
        circuitBreakerActive: !this.circuitBreaker.canRequest(),
        etag: cached.etag
      };
    }

    // 2. Intentar R2 si circuit breaker lo permite
    if (this.circuitBreaker.canRequest()) {
      try {
        const r2Data = await this.fetchFromR2<T>(dataset, cached?.etag ?? null);
        
        if (r2Data !== null) {
          this.circuitBreaker.recordSuccess();
          this.stats.r2Requests++;
          
          // Guardar en caché
          await this.saveToCache(dataset, r2Data.data, r2Data.etag, r2Data.sizeBytes, 'r2');
          
          return {
            data: r2Data.data,
            source: 'r2',
            responseTime: performance.now() - startTime,
            circuitBreakerActive: false,
            etag: r2Data.etag
          };
        }
        
        // 304 Not Modified - usar caché
        if (cached) {
          this.stats.cacheHits++;
          return {
            data: cached.data as T,
            source: 'cache',
            responseTime: performance.now() - startTime,
            circuitBreakerActive: false,
            etag: cached.etag
          };
        }
      } catch (error) {
        // AUD-E04: diferenciar AbortError (timeout esperado) de error inesperado
        const isAbort =
          error instanceof DOMException && error.name === 'AbortError';

        if (isAbort) {
          // Timeout esperado → silencioso, sin console.warn, sin Sentry
        } else {
          // Error inesperado → reportar a Sentry antes de continuar
          import('@sentry/react').then(({ captureException }) => {
            captureException(error, {
              extra: {
                dataset,
                context: 'R2DataService.fetchFromR2 — error inesperado',
              },
            });
          });
        }

        this.circuitBreaker.recordError();
        this.stats.r2Errors++;
      }
    }

    // 3. Fallback a bundle estático
    try {
      const staticData = await this.fetchFromStatic<T>(dataset);
      this.stats.staticRequests++;
      
      // Guardar en caché
      await this.saveToCache(dataset, staticData, null, 0, 'static');
      
      return {
        data: staticData,
        source: 'static',
        responseTime: performance.now() - startTime,
        circuitBreakerActive: !this.circuitBreaker.canRequest(),
        etag: null
      };
    } catch (staticError) {
      // Si también falla el estático y tenemos caché (aunque expirado), usarlo
      if (cached) {
        console.warn(`[R2DataService] Usando caché expirado para ${dataset}`);
        return {
          data: cached.data as T,
          source: 'cache',
          responseTime: performance.now() - startTime,
          circuitBreakerActive: !this.circuitBreaker.canRequest(),
          etag: cached.etag
        };
      }
      
      throw new Error(`No se pudo obtener dataset ${dataset} de ninguna fuente`, { cause: staticError });
    }
  }

  /**
   * Fetch desde R2 Worker con soporte de gzip
   */
  private async fetchFromR2<T>(
    dataset: R2Dataset, 
    etag: string | null
  ): Promise<{ data: T; etag: string | null; sizeBytes: number } | null> {
    const url = `${R2_BASE_URL}/${dataset}.json.gz`;
    
    const headers: Record<string, string> = {
      'Accept': 'application/json',
      'Accept-Encoding': 'gzip'
    };
    
    if (etag) {
      headers['If-None-Match'] = etag;
    }
    
    const { response } = await fetchWithBackoff(url, {
      timeoutMs: R2_TIMEOUT,
      maxRetries: 0, // AUD-E04: sin reintentos internos; la cascada de fallback es el mecanismo de resiliencia
      headers,
    });
    
    // 304 Not Modified
    if (response.status === 304) {
      return null;
    }
    
    if (!response.ok) {
      throw new Error(`R2 respondió con status ${response.status}`);
    }
    
    const data = await response.json() as T;
    const responseEtag = response.headers.get('ETag');
    const contentLength = parseInt(response.headers.get('Content-Length') || '0', 10);
    
    return { data, etag: responseEtag, sizeBytes: contentLength };
  }

  /**
   * Fetch desde bundle estático local
   */
  private async fetchFromStatic<T>(dataset: R2Dataset): Promise<T> {
    // Mapear dataset a ruta local
    const staticPath = this.getStaticPath(dataset);
    const response = await fetch(staticPath);
    
    if (!response.ok) {
      throw new Error(`Bundle estático no encontrado: ${staticPath}`);
    }
    
    return await response.json() as T;
  }

  /**
   * Mapea dataset R2 a ruta de bundle estático
   */
  private getStaticPath(dataset: R2Dataset): string {
    const mapping: Record<R2Dataset, string> = {
      'dera/all-dera': `${STATIC_BUNDLE_BASE}/dera-dexie/all-dera.json`,
      'dera/health': `${STATIC_BUNDLE_BASE}/dera-dexie/health.json`,
      'dera/education': `${STATIC_BUNDLE_BASE}/dera-dexie/education.json`,
      'dera/security': `${STATIC_BUNDLE_BASE}/dera-dexie/security.json`,
      'dera/municipal': `${STATIC_BUNDLE_BASE}/dera-dexie/municipal.json`,
      'dera/energy': `${STATIC_BUNDLE_BASE}/dera-dexie/energy.json`,
      'dera/emergency': `${STATIC_BUNDLE_BASE}/dera-dexie/emergency.json`,
      'ine/municipios': `${STATIC_BUNDLE_BASE}/ine/municipios.json`,
      'nga/nga-all': `${STATIC_BUNDLE_BASE}/nga/nga-all.json`,
      'nga/nga-landform': `${STATIC_BUNDLE_BASE}/nga/nga-landform.json`,
      'nga/nga-populatedplace': `${STATIC_BUNDLE_BASE}/nga/nga-populatedplace.json`,
      'nga/nga-hydrography': `${STATIC_BUNDLE_BASE}/nga/nga-hydrography.json`,
      'nga/nga-landcover': `${STATIC_BUNDLE_BASE}/nga/nga-landcover.json`,
      'nga/nga-transportnetwork': `${STATIC_BUNDLE_BASE}/nga/nga-transportnetwork.json`
    };
    
    return mapping[dataset] || `${STATIC_BUNDLE_BASE}/${dataset}.json`;
  }

  /**
   * Obtiene entrada de caché IndexedDB
   */
  private async getFromCache<T>(dataset: R2Dataset): Promise<CacheEntry<T> | null> {
    try {
      const entry = await this.db.datasets.get(dataset);
      return entry as CacheEntry<T> | null;
    } catch {
      return null;
    }
  }

  /**
   * Guarda en caché IndexedDB
   */
  private async saveToCache<T>(
    dataset: R2Dataset, 
    data: T, 
    etag: string | null,
    sizeBytes: number,
    source: 'r2' | 'static'
  ): Promise<void> {
    try {
      await this.db.datasets.put({
        datasetId: dataset,
        data,
        updatedAt: Date.now(),
        etag,
        sizeBytes,
        source
      });
    } catch (error) {
      console.warn('[R2DataService] Error guardando en caché:', error);
    }
  }

  /**
   * Verifica si la entrada de caché ha expirado
   */
  private isCacheExpired(updatedAt: number): boolean {
    return Date.now() - updatedAt > CACHE_TTL;
  }

  /**
   * Obtiene el manifiesto de R2 (con caché)
   */
  async getManifest(): Promise<R2Manifest | null> {
    if (this.manifest) return this.manifest;
    
    if (!this.circuitBreaker.canRequest()) {
      return null;
    }
    
    try {
      const response = await fetch(`${R2_BASE_URL}/manifest.json`, {
        signal: AbortSignal.timeout(R2_TIMEOUT)
      });
      
      if (response.ok) {
        this.manifest = await response.json();
        this.circuitBreaker.recordSuccess();
        return this.manifest;
      }
    } catch (error) {
      console.warn('[R2DataService] Error obteniendo manifest:', error);
      this.circuitBreaker.recordError();
    }
    
    return null;
  }

  /**
   * Verifica la salud del servicio R2
   */
  async checkHealth(): Promise<{ healthy: boolean; latency: number }> {
    const start = performance.now();
    
    try {
      const response = await fetch(`${WORKER_BASE}/health`, {
        signal: AbortSignal.timeout(5000)
      });
      
      const latency = performance.now() - start;
      
      if (response.ok) {
        const data = await response.json();
        return { healthy: data.status === 'healthy', latency };
      }
      
      return { healthy: false, latency };
    } catch {
      return { healthy: false, latency: performance.now() - start };
    }
  }

  /**
   * Obtiene estadísticas del servicio
   */
  async getStats(): Promise<R2ServiceStats> {
    this.stats.circuitState = this.circuitBreaker.getState();
    this.stats.consecutiveErrors = this.circuitBreaker.getErrorCount();
    
    try {
      const entries = await this.db.datasets.toArray();
      this.stats.cachedDatasets = entries.length;
      this.stats.totalCacheSize = entries.reduce((sum, e) => sum + e.sizeBytes, 0);
    } catch {
      // IndexedDB no disponible
    }
    
    return { ...this.stats };
  }

  /**
   * Limpia la caché de un dataset específico o todos
   */
  async clearCache(dataset?: R2Dataset): Promise<void> {
    try {
      if (dataset) {
        await this.db.datasets.delete(dataset);
      } else {
        await this.db.datasets.clear();
      }
    } catch (error) {
      console.warn('[R2DataService] Error limpiando caché:', error);
    }
  }

  /**
   * Precarga datasets en caché
   */
  async preloadDatasets(datasets: R2Dataset[]): Promise<void> {
    await Promise.all(datasets.map(ds => this.fetchDataset(ds)));
  }

  /**
   * Resetea el Circuit Breaker manualmente
   */
  resetCircuitBreaker(): void {
    this.circuitBreaker.reset();
  }
}

// =============================================================================
// SINGLETON Y EXPORTS
// =============================================================================

let r2ServiceInstance: R2DataService | null = null;

/**
 * Obtiene instancia singleton del servicio R2
 */
export function getR2DataService(): R2DataService {
  if (!r2ServiceInstance) {
    r2ServiceInstance = new R2DataService();
  }
  return r2ServiceInstance;
}

export default R2DataService;
